import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Navigate, Outlet, useSearchParams } from 'react-router';

import { useTypedParams } from '@sorare/routing';

import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { navLabels } from '@sorare/core/src/lib/glossary';

import type {
  FixtureCompetitionTrckGrpLayoutQuery,
  FixtureCompetitionTrckGrpLayoutQueryVariables,
} from './__generated__/index.graphql';

const FIXTURE_COMPETITION_TRCKGRP_LAYOUT_QUERY = gql`
  query FixtureCompetitionTrckGrpLayoutQuery(
    $competition: String!
    $trckGrp: String!
  ) {
    so5 {
      so5LeaderboardGroupInterface(slug: $competition) {
        slug
        so5LeagueTrackGroup(slug: $trckGrp) {
          slug
          so5LeagueTracks {
            slug
            seasonality
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  FixtureCompetitionTrckGrpLayoutQuery,
  FixtureCompetitionTrckGrpLayoutQueryVariables
>;
export const FixtureCompetitionTrckGrpLayout = <
  Params extends {
    eventType?: string;
    fixture: string;
    competition: string;
    trckGrp: string;
    trck?: string;
    contender?: string;
  },
>() => {
  const [searchParams] = useSearchParams();
  const seasonality = searchParams.get('seasonality');

  const { generateSportContextPath } = useSportContext();
  const {
    fixture,
    eventType = '',
    competition,
    trckGrp,
    trck,
  } = useTypedParams<Params>();

  const { data } = useQuery(FIXTURE_COMPETITION_TRCKGRP_LAYOUT_QUERY, {
    variables: { competition, trckGrp },
    skip: !!trck,
  });

  const { so5LeaderboardGroupInterface: so5League } = data?.so5 || {};
  const { so5LeagueTrackGroup } = so5League || {};

  const targettedTrckSlug =
    so5LeagueTrackGroup?.so5LeagueTracks.find(
      track => track.seasonality === seasonality
    )?.slug ||
    so5LeagueTrackGroup?.so5LeagueTracks.at(0)?.slug ||
    '';

  if (!trck) {
    return (
      <Navigate
        replace
        to={generateSportContextPath(
          ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK,
          {
            params: {
              fixture,
              eventType,
              competition,
              trckGrp,
              trck: targettedTrckSlug,
            },
          }
        )}
      />
    );
  }

  return (
    <>
      <PageHeader
        text={navLabels.competitions}
        backTo={generateSportContextPath(
          ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
          {
            params: {
              fixture,
              eventType,
            },
          }
        )}
      />
      <Outlet />
    </>
  );
};
